import { Component, type ReactNode, type ErrorInfo } from 'react';
import { consumeError } from '../services/clientLogging/clientLogging.js';

interface LazyLoadErrorBoundaryProps {
  children: ReactNode;
  componentName?: string;
  showRetry?: boolean;
  onRetry?: () => void;
}

interface LazyLoadErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

/**
 * Enhanced Error Boundary specifically for lazy loading failures
 * Handles race conditions, network failures, and provides retry functionality
 */
class LazyLoadErrorBoundary extends Component<LazyLoadErrorBoundaryProps, LazyLoadErrorBoundaryState> {
  constructor(props: LazyLoadErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(_error: Error): Partial<LazyLoadErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error: _error };
  }

  componentDidCatch(_error: Error, errorInfo: ErrorInfo) {
    console.error('LazyLoadErrorBoundary caught an error:', _error, errorInfo);

    this.setState({
      error: _error,
      errorInfo,
      hasError: true
    });

    consumeError(_error, {
      errorBoundary: 'LazyLoadErrorBoundary',
      componentName: this.props.componentName || 'Unknown',
      retryCount: this.state.retryCount
    });
  }

  handleRetry = async () => {
    // Import chunk error utilities
    const { checkForNewBuild, areChunksStale } = await import('../utils/chunkErrorHandler.js');

    // Check if this is due to stale chunks
    if (areChunksStale() || await checkForNewBuild()) {
      sessionStorage.setItem('chunk_error_redirect', window.location.href);
      window.location.reload();
      return;
    }

    // Clear error state and increment retry count
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));

    // Force re-render of lazy component
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  handleReload = () => {
    // Full page reload to clear any corrupt state
    window.location.reload();
  };

  isChunkLoadError = (error: Error | null): boolean => {
    const message = error?.message?.toLowerCase() || '';
    const stack = error?.stack?.toLowerCase() || '';

    return (
      error?.name === 'SyntaxError' ||
      message.includes('unexpected token') ||
      message.includes('loading chunk') ||
      message.includes('failed to fetch') ||
      message.includes('network error') ||
      stack.includes('webpackchunkname')
    );
  };

  render() {
    if (this.state.hasError) {
      const { error } = this.state;
      const { componentName = 'Component', showRetry = true } = this.props;
      const isNetworkError = this.isChunkLoadError(error);

      return (
        <div className="lazy-load-error-boundary p-6 m-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {isNetworkError ? 'Loading Error' : 'Component Error'}
              </h3>
              <div className="mt-2 text-sm text-red-700">
                {isNetworkError ? (
                  <>
                    Failed to load {componentName}. This usually happens due to network issues or temporary server problems.
                  </>
                ) : (
                  <>
                    An error occurred while loading {componentName}.
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex space-x-2">
            {showRetry && this.state.retryCount < 3 && (
              <button
                onClick={this.handleRetry}
                className="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-2 rounded text-sm font-medium transition-colors"
                type="button"
              >
                Try Again {this.state.retryCount > 0 && `(${this.state.retryCount + 1})`}
              </button>
            )}

            <button
              onClick={this.handleReload}
              className="bg-slate-100 hover:bg-slate-200 text-slate-800 px-3 py-2 rounded text-sm font-medium transition-colors"
              type="button"
            >
              Reload Page
            </button>
          </div>

          {import.meta.env.DEV && (
            <details className="mt-4">
              <summary className="text-sm text-red-600 cursor-pointer">
                Technical Details (Development Only)
              </summary>
              <pre className="mt-2 text-xs bg-red-100 p-2 rounded overflow-auto">
                {error?.message}
                {'\n\n'}
                {error?.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default LazyLoadErrorBoundary;
